import { enableReduxLogger } from '@chegg-wt/core/utils/env'
import { applyMiddleware, compose, createStore, Store, StoreCreator } from 'redux'
import { createLogger } from 'redux-logger'
import { createLogicMiddleware } from 'redux-logic'

// EasyBib Data APIs
// import * as citationQualityApi from 'easybib/apis/citation-quality'
import { SiteEnv } from '@chegg-wt/core/types/site'
import { Config } from '../config'
import { formatApi, setHeaderCookieName } from '../api/rcaFormatApi'
import { NodeEnvs } from '../constants/global'
import { bff } from '../api/bff'
import { oneGraph } from '../api/oneGraph'
import { googleApi } from '../api/googleDocs'
import {
  getMigrateStatus,
  login,
  logout,
  refreshToken,
  getMigrateStatusWithoutID,
} from '../api/auth'
import { AppRouteCreators } from '../types/core'
import { wtaMiddleware } from './instrumentation/rio'
import rootLogic from './rootLogic'
// EasyBib redux resources
// import * as actionCreators from './actions/actions.types'
import rootReducer from './rootReducer'
// Server/Client hinting
// import isWindowDefined from 'utils/isWindowDefined'
// Typings
import { AppState as ImportedAppState } from './types'

import { opMiddleware } from './optimizely/middleware'
import { errorLoggerMiddleware } from './errors/middleware'
import { newRelicMiddleware } from './instrumentation/newRelic'

export type AppState = ImportedAppState

export const APIs = {
  bff,
  formatApi,
  googleApi,
  // citationQualityApi,
  login,
  logout,
  refreshToken,
  getMigrateStatus,
  getMigrateStatusWithoutID,
  oneGraph,
}

export interface LogicDeps {
  api: typeof APIs
  routes: AppRouteCreators
  siteEnv: SiteEnv
  superauthRedirectUri: string
}

const createNewStore = (
  routes: AppRouteCreators,
  siteEnv: SiteEnv,
  preloadedState = {}
): Store<AppState> => {
  setHeaderCookieName()
  const logicDeps: LogicDeps = {
    api: APIs,
    routes,
    siteEnv,
    superauthRedirectUri: Config.superauthRedirectUri,
  }

  const logicMiddleware = createLogicMiddleware(rootLogic, logicDeps)
  const enhancers = [
    applyMiddleware(
      errorLoggerMiddleware,
      logicMiddleware,
      opMiddleware,
      wtaMiddleware,
      newRelicMiddleware
    ),
  ]
  if (Config.nodeEnv !== NodeEnvs.PRODUCTION && Config.nodeEnv !== NodeEnvs.TEST) {
    if (enableReduxLogger()) {
      const logger = createLogger({ collapsed: true })
      enhancers.push(applyMiddleware(logger))
    }
    /* eslint-disable no-underscore-dangle */
    if (typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__) {
      enhancers.push(window.__REDUX_DEVTOOLS_EXTENSION__())
    }
  }

  const configureStore = compose(...enhancers)(createStore) as StoreCreator
  return configureStore(rootReducer, preloadedState)
}

export let store: Store<AppState> // eslint-disable-line import/no-mutable-exports

const State = (
  routes: AppRouteCreators,
  siteEnv: SiteEnv,
  preloadedState = {}
): Store<AppState> => {
  // Always make a new store if server, otherwise state is shared between requests
  if (typeof window === 'undefined') {
    return createNewStore(routes, siteEnv, preloadedState)
  }

  // Create store if unavailable on the client
  if (!store) {
    store = createNewStore(routes, siteEnv, preloadedState)
  }

  return store
}
export default State
